import { filterActiveMasterRows } from "../../memberships/utils/masterLookupUtils";

export const PROPERTY_STATUS_TONES = ["mint", "slate", "coral", "teal"];

const norm = (v) => String(v ?? "").trim().toLowerCase();

const sortedStatusMasters = (statusMasters = []) =>
  filterActiveMasterRows(statusMasters).sort((a, b) =>
    String(a.status_name ?? "").localeCompare(String(b.status_name ?? ""), undefined, {
      sensitivity: "base",
    }),
  );

/** Stable tone per status id — matches metric card colours on the dashboard. */
export function getPropertyStatusTone(statusId, statusMasters = []) {
  const id = Number(statusId);
  if (!Number.isFinite(id) || id <= 0) {
    return PROPERTY_STATUS_TONES[0];
  }

  const masters = sortedStatusMasters(statusMasters);
  const index = masters.findIndex((m) => Number(m.id) === id);
  if (index >= 0) {
    return PROPERTY_STATUS_TONES[index % PROPERTY_STATUS_TONES.length];
  }

  return PROPERTY_STATUS_TONES[id % PROPERTY_STATUS_TONES.length];
}

export function formatInr(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * Build metric cards from active mst_property_statuses rows + asset list.
 * - Total Assets (fixed)
 * - One card per active status in master (hidden when status inactive / soft-deleted)
 * - Total Asset Value (fixed)
 */
export function buildPropertyAssetMetricItems(assets = [], statusMasters = []) {
  const rows = Array.isArray(assets) ? assets : [];
  const masters = sortedStatusMasters(statusMasters);

  let totalValue = 0;
  const countByStatusId = new Map();

  for (const m of masters) {
    countByStatusId.set(Number(m.id), 0);
  }

  for (const row of rows) {
    const value = Number(row.purchase_value);
    if (Number.isFinite(value)) totalValue += value;

    const statusId = Number(row.property_status_id);
    if (countByStatusId.has(statusId)) {
      countByStatusId.set(statusId, countByStatusId.get(statusId) + 1);
      continue;
    }

    const name = norm(row.status_name);
    if (!name) continue;
    const match = masters.find((m) => norm(m.status_name) === name);
    if (match) {
      const id = Number(match.id);
      countByStatusId.set(id, (countByStatusId.get(id) ?? 0) + 1);
    }
  }

  const items = [
    {
      key: "total-assets",
      label: "Total Assets",
      value: rows.length,
      tone: "teal",
    },
  ];

  masters.forEach((m, index) => {
    const id = Number(m.id);
    items.push({
      key: `status-${id}`,
      label: m.status_name,
      value: countByStatusId.get(id) ?? 0,
      tone: PROPERTY_STATUS_TONES[index % PROPERTY_STATUS_TONES.length],
    });
  });

  items.push({
    key: "total-asset-value",
    label: "Total Asset Value",
    value: formatInr(totalValue),
    tone: "teal",
  });

  return items;
}
